import React, { useCallback, useEffect, useRef, useState } from 'react';
import './_contentGen.scss';
import { PromptLI, SuggestionLI } from './UtilComponents';
import LocaleHoc from '../../LocaleHoc';
import * as constants from './constants';
import JsonAssembler, { renderChat } from './jsonAssembler';
import useWebsocket from '../hooks/useWebsocket';
import CapRow from '../../CapRow';
import CapButton from '../../CapButton';
import AiraContainer from '../UtilComponents/AiraContainer';
import ErrorBar from '../UtilComponents/ErrorBar';
import { rootClsPrefix } from '../constants';
import { AiraCorner } from '../UtilComponents/Icons';

const { clsPrefix } = constants;

const ContentGenerationBot = ({
  text,
  setText,
  popupWidth,
  iconPlacement,
  rootStyle,
  textTypeAMessage,
  textError,
  textAiraTitle,
  textAiraTooltip,
  textCharacters,
  textReplaceWithThis,
  textResetToDefault,
  textMoreLikeThis,
  textGenerateMore,
  textReplaced,
  iconSize,
}) => {
  const chatCompletionRef = useRef();
  const originalPrompt = useRef();
  const [chat, setChat] = useState([]);
  const [renderedChat, setRenderedChat] = useState([]);
  const [showError, setShowError] = useState(false);
  const [replacedWith, setReplacedWith] = useState();
  const messages = useRef({});

  const onMessage = useCallback(
    msg => {
      messages.current[msg.id]?.assemblers?.[msg.index]?.processMessage?.(msg);
      if (msg.type === constants.MESSAGE_RENDER) {
        setRenderedChat(renderChat(chat));
      }
    },
    [chat],
  );

  const onError = useCallback(() => {
    setShowError(true);
    setChat([]);
  }, []);

  const { sendMessage, resetConnection } = useWebsocket(constants.WEBSOCKET_URL, onMessage, onError);

  const getSuggestions = (query, isGenerateMore = false) => {
    setShowError(false);
    const msgId = Date.now();
    const assemblers = [];
    for (let i = 0; i < constants.N_CHOICES; i++)
      assemblers.push(new JsonAssembler());
    const newMsg = {
      query,
      assemblers,
    };
    messages.current[msgId] = newMsg;
    const newChat = [...chat, newMsg];

    sendMessage({
      id: msgId,
      type: constants.MESSAGE_GET_SUGGESTIONS,
      generateMore: isGenerateMore,
      query,
    });
    setChat(newChat);
  };
  useEffect(
    () => {
      setRenderedChat(renderChat(chat));
    },
    [chat],
  );

  const onShow = () => {
    originalPrompt.current = text;
    if (text.length) {
      getSuggestions(text);
    }
  };

  const scrollToBottomWithDelay = () => {
    setTimeout(() => {
      chatCompletionRef.current?.scroll?.(
        0,
        chatCompletionRef.current.scrollHeight,
      );
    }, 50);
  };
  const onHide = () => {
    setChat([]);
    setShowError(false);
    setReplacedWith(null);
    resetConnection();
  };
  const generateMoreClick = () => {
    if (text) {
      scrollToBottomWithDelay();
      getSuggestions(text, true);
    }
  };

  const resetReplacedTextToOriginal = () => {
    setText(originalPrompt.current);
    setReplacedWith(null);
  };
  return (
    <AiraContainer
      titlePadding="0.5rem"
      popupWidth={popupWidth}
      Icon={AiraCorner}
      iconPlacement={iconPlacement}
      popupPlacement={{ type: 'anchor-br' }}
      onHide={onHide}
      onShow={onShow}
      title={textAiraTitle}
      titleDescription={textAiraTooltip}
      rootStyle={rootStyle}
      iconSize={iconSize}
    >
      {renderedChat?.length === 0 && (
        <CapRow className={`${clsPrefix}-type-message`}>
          {textTypeAMessage}
        </CapRow>
      )}
      <div
        ref={chatCompletionRef}
        className={`${rootClsPrefix}-no-scrollbar ${clsPrefix}-chat-container`}
      >
        {renderedChat?.length > 0 &&
          renderedChat.map((msg, index) => {
            if (msg?.type === 'user') {
              return (
                <PromptLI
                  key={index}
                  content={msg}
                  moreLikeThis={index > 0}
                  showReset={replacedWith}
                  reset={resetReplacedTextToOriginal}
                  textMoreLikeThis={textMoreLikeThis}
                  textResetToDefault={textResetToDefault}
                  textCharacters={textCharacters}
                />
              );
            }
            if (msg?.type === 'bot') {
              return (
                <SuggestionLI
                  textMoreLikeThis={textMoreLikeThis}
                  textReplaced={textReplaced}
                  key={index}
                  content={msg?.content}
                  loading={!msg?.suggestionLoaded}
                  replace={() => {
                    setText(msg?.content?.content);
                    setReplacedWith(index);
                  }}
                  active={replacedWith === index}
                  moreLikeThis={() => {
                    scrollToBottomWithDelay();
                    getSuggestions(msg?.content?.content);
                  }}
                  textReplaceWithThis={textReplaceWithThis}
                  textCharacters={textCharacters}
                />
              );
            }
          })}
        {showError && <ErrorBar textError={textError} />}
      </div>
      <CapButton
        className={`${clsPrefix}-generate-suggestions`}
        type="flat"
        isAddBtn
        disabled={
          !text.length ||
          !chat[chat.length - 1]?.assemblers?.[0]?.isContentReceived()
        }
        onClick={generateMoreClick}
      >
        {textGenerateMore}
      </CapButton>
    </AiraContainer>
  );
};

ContentGenerationBot.defaultProps = {
  text: '',
  setText: () => { },
  popupWidth: '42rem',
};

export default LocaleHoc(ContentGenerationBot, {
  key: 'CapAskAira.ContentGenerationBot',
});
